import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Loading from '../components/common/Loading';
import { assessmentAPI } from '../services/api';
import { toast } from 'react-hot-toast';

const MCQ_QUESTION_TYPES = new Set([
  'mcq',
  'multiple_choice',
  'multiple-choice',
  'objective',
  'quiz',
]);

const normalizeQuestionType = (question) => {
  const rawType = question?.type || question?.question_type || question?.answer_type || question?.format || 'mcq';
  const normalizedType = String(rawType).trim().toLowerCase();

  if (MCQ_QUESTION_TYPES.has(normalizedType)) return 'mcq';
  return 'mcq';
};

const AssessmentPage = () => {
  const { moduleId } = useParams();
  const [searchParams] = useSearchParams();
  const roadmapId = searchParams.get('roadmapId');
  const navigate = useNavigate();
  
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const roadmapRefreshToken = result?.roadmapUpdatedAt || Date.now();

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!roadmapId || !moduleId) {
        toast.error('Missing roadmap or module ID');
        navigate('/roadmap');
        return;
      }

      try {
        setIsLoading(true);
        const assessmentData = await assessmentAPI.getModuleAssessment(roadmapId, moduleId);
        
        setAssessment(assessmentData);
        setStartTime(new Date());
        
        // Initialize answers
        const initAnswers = {};
        assessmentData.questions.forEach(q => {
          initAnswers[q.id.toString()] = '';
        });
        setAnswers(initAnswers);
        
      } catch (error) {
        console.error('Failed to fetch assessment:', error);
        toast.error('Failed to load assessment');
        navigate('/roadmap');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessment();
  }, [roadmapId, moduleId, navigate]);

  // Timer effect
  useEffect(() => {
    if (!startTime) return;
    
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((new Date() - startTime) / 1000 / 60)); // minutes
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Check if all questions answered
    const unansweredQuestions = assessment.questions.filter(q => 
      !answers[q.id.toString()] || answers[q.id.toString()].trim() === ''
    );

    if (unansweredQuestions.length > 0) {
      const proceed = window.confirm(
        `You have ${unansweredQuestions.length} unanswered questions. Submit anyway?`
      );
      if (!proceed) return;
    }

    try {
      setIsSubmitting(true);
      
      const submission = {
        answers: answers,
        timeSpentMinutes: timeSpent || 1
      };

      const result = await assessmentAPI.submitAssessment(roadmapId, moduleId, submission);
      setResult(result);
      
      const message = result.passed 
        ? `🎉 Assessment Passed! Score: ${result.score}%` 
        : `Score: ${result.score}% - Need ${assessment.passScore}% to pass`;
      
      if (result.passed) {
        toast.success(message);
      } else {
        toast.error(message);
      }

    } catch (error) {
      console.error('Failed to submit assessment:', error);
      toast.error('Failed to submit assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setTimeSpent(0);
    setStartTime(new Date());
    
    // Reset answers
    const resetAnswers = {};
    assessment.questions.forEach(q => {
      resetAnswers[q.id.toString()] = '';
    });
    setAnswers(resetAnswers);
  };

  const updateAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId.toString()]: value,
    }));
  };

  if (isLoading) return <Loading text="Loading assessment..." />;
  if (!assessment) return <div>Assessment not found</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-8 max-w-4xl mx-auto">
        {!result ? (
          // Assessment Form
          <Card>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{assessment.title}</h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span>Duration: {assessment.durationMinutes} min</span>
                  <span>Pass Score: {assessment.passScore}%</span>
                  <span>Questions: {assessment.questions.length}</span>
                  {assessment.userAttempts > 0 && (
                    <span className="text-blue-600">Attempts: {assessment.userAttempts}</span>
                  )}
                </div>
                {timeSpent > 0 && (
                  <div className="mt-2 text-sm font-medium text-orange-600">
                    Time: {timeSpent} minutes
                  </div>
                )}
              </div>
              <Link to="/roadmap" className="text-blue-600 hover:underline">
                ← Back to Roadmap
              </Link>
            </div>

            <div className="space-y-6">
              {assessment.questions.map((q, idx) => (
                <div key={q.id} className="p-6 bg-white border border-gray-200 rounded-lg">
                  <div className="mb-4">
                    <h3 className="font-semibold text-lg text-gray-900">
                      Question {idx + 1}
                    </h3>
                    <p className="mt-2 text-gray-700 leading-relaxed">
                      {q.prompt}
                    </p>
                  </div>

                  {normalizeQuestionType(q) === 'mcq' && (
                    <div className="space-y-3">
                      {(Array.isArray(q.options) ? q.options : []).map((option, optIdx) => (
                        <label key={optIdx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100">
                          <input
                            type="radio"
                            name={`question-${q.id}`}
                            value={option}
                            checked={answers[q.id.toString()] === option}
                            onChange={(e) => updateAnswer(q.id, e.target.value)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-4">
              <button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-lg font-medium text-white ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
              </button>
              
              <Link 
                to="/roadmap" 
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Link>
            </div>
          </Card>
        ) : (
          // Results
          <Card>
            <div className="text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                result.passed ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <span className={`text-3xl font-bold ${
                  result.passed ? 'text-green-600' : 'text-red-600'
                }`}>
                  {result.score}%
                </span>
              </div>
              
              <h2 className={`text-2xl font-bold mb-4 ${
                result.passed ? 'text-green-600' : 'text-red-600'
              }`}>
                {result.passed ? '🎉 Assessment Passed!' : '❌ Assessment Failed'}
              </h2>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="font-bold text-lg">{result.score}%</div>
                    <div className="text-sm text-gray-600">Your Score</div>
                  </div>
                  <div>
                    <div className="font-bold text-lg">{result.correctAnswers}/{result.totalQuestions}</div>
                    <div className="text-sm text-gray-600">Correct</div>
                  </div>
                  <div>
                    <div className="font-bold text-lg">{assessment.passScore}%</div>
                    <div className="text-sm text-gray-600">Required</div>
                  </div>
                  <div>
                    <div className="font-bold text-lg">{result.attemptNumber}</div>
                    <div className="text-sm text-gray-600">Attempt #</div>
                  </div>
                </div>
              </div>

              {result.feedback && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-blue-900 mb-2">Feedback</h3>
                  <p className="text-blue-800">{result.feedback}</p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-gray-900 font-semibold mb-3">Readiness</h3>
                  <p className="text-sm text-gray-500 mb-2">Overall readiness is calculated from your performance across skill areas.</p>
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="text-3xl font-bold text-gray-900">{result.readinessScore ?? result.score}%</span>
                    <span className="text-sm text-gray-500">readiness score</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Hours to improve</p>
                      <p className="text-lg font-semibold text-gray-900">{result.timeEstimate?.estimated_hours_to_improve ?? result.estimatedUpskillHours ?? 0}h</p>
                    </div>
                    <div className="bg-slate-100 rounded-lg p-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Days to readiness</p>
                      <p className="text-lg font-semibold text-gray-900">{result.timeEstimate?.estimated_days_to_readiness ?? result.estimatedDaysToReadiness ?? 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-gray-900 font-semibold mb-3">Skill summary</h3>
                  <div className="space-y-3">
                    {result.skillScores?.length > 0 ? (
                      result.skillScores.map((skill) => (
                        <div key={skill.name} className="flex justify-between items-center gap-4 p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{skill.name}</p>
                            <p className="text-xs text-gray-500">{skill.correctAnswers}/{skill.totalQuestions} correct</p>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{skill.percentage}%</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No skill score details available yet.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {result.strongSkills?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 font-semibold mb-2">Strong</p>
                    <div className="flex flex-wrap gap-2">
                      {result.strongSkills.map((skill) => (
                        <span key={skill} className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {result.mediumSkills?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-700 font-semibold mb-2">Developing</p>
                    <div className="flex flex-wrap gap-2">
                      {result.mediumSkills.map((skill) => (
                        <span key={skill} className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {result.weakSkills?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-700 font-semibold mb-2">Needs improvement</p>
                    <div className="flex flex-wrap gap-2">
                      {result.weakSkills.map((skill) => (
                        <span key={skill} className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 justify-center">
                {result.passed ? (
                  <Link 
                    to={`/roadmap?refresh=${encodeURIComponent(roadmapRefreshToken)}`}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                  >
                    Continue to Next Module
                  </Link>
                ) : (
                  <button 
                    onClick={handleRetry}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                )}
                
                <Link 
                  to={`/roadmap?refresh=${encodeURIComponent(roadmapRefreshToken)}`}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Back to Roadmap
                </Link>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AssessmentPage;
